/**
 * Google Tag Manager / GA4 analytics for the Bolsius theme, scoped to the events listed in the
 * Measurement Plan doc (BOL-58). Reads page, shop, and cart config from `window.GTMConfig` (set
 * in `snippets/gtm-valantic.liquid`). Logged-in `accountId` comes from `#gtm-customer-ids`
 * (JSON), consumed once for `pageview`, not stored globally.
 * Loaded as `type="module"` with `fetchpriority="low"` so parsing does not block the main thread.
 *
 * The checkout funnel (`begin_checkout` … `purchase`) is delivered separately as
 * `assets/gtm_checkout.js`, a Shopify Custom Web Pixel. `review_submit` remains out of scope
 * because this store has no reviews app. Product lists are tracked here with `view_item_list` when
 * they enter the viewport and `select_item` when a product link inside one is activated.
 * "Load more"-appended pages (`paginated-list.js`'s `theme:results_appended`) get their own
 * `view_item_list` for just the newly-appended batch, plus `select_item` binding on those cards
 * — the initial page-load batch alone previously left later pages with neither event.
 *
 * `theme:text_message`/`theme:validation_message` are dispatched by `contact-form.js` and
 * `email-signup.js` (native constraint-validation errors client-side, `form.errors` server-side).
 * Doc-required events with no dispatcher at all, because no theme component/page can reach them:
 *   - `sign_up` — this store uses Shopify's hosted Customer Accounts (`shopify.com/.../account`),
 *     not the theme's own `/account/register` template, so no theme JS runs on that page; would
 *     need a Shopify Customer Account UI Extension instead.
 * `login` is approximated instead of hooked directly, for the same hosted-accounts reason —
 * see `trackLoginIfNeeded`.
 * `add_to_wishlist` is wired from Swym's PDP button (`#swym-custom-pdp-button`) via its own
 * `aria-pressed` toggle — see `wishlistListener`.
 * `navigation_use` is wired from both `header-drawer.js`'s mobile menu link clicks and
 * `header-menu.js`'s desktop mega-menu link clicks, both dispatching the same `navigation:click`.
 * `sort_use` is wired from `facets.js`'s `SortingFilterComponent.updateFilterAndSorting` (fires
 * on `change` of the `sort_by` radio/select) — `sort_option` is Shopify's own `sort_by` slug
 * (e.g. `price-descending`), reshaped by `sortUseListener`'s `normalizeSortOption` into the doc's
 * `<key> <direction>` example format (`price desc`), not the input's localized display label.
 * `filter_use` is wired from `facets.js`'s `FacetInputsComponent.updateFilters` (fires only when
 * a filter checkbox is checked ON, not when unchecked) and, separately, from
 * `PriceFacetComponent.updatePriceFilterAndResults` (fires on every price range change, with
 * `filter_group: 'price'` and the rendered facet-status text as `filter_option`, e.g. "€10 - €50").
 * For checkboxes, `filter_group` is the raw `filter.v.*`/`filter.p.m.*` param name (prefix
 * stripped); `filter_option` is the checked input's `aria-label` (Shopify's own readable option
 * label), falling back to `value` only if that attribute is ever absent. `availability`'s raw
 * `1`/`0` `value` is remapped to `in_stock`/`out_of_stock` by `normalizeFilterOption` (doc requires
 * `filter_option` "always English") — that
 * mapping is safe only because availability is Shopify's own fixed boolean convention, not
 * merchant-entered content; a real color/size facet's value (e.g. "Blauw") is left as-is, since
 * there's no data source to translate arbitrary merchant-entered option values into English (same
 * open limitation as `item_name`, shared with the jack-wolfskin reference implementation).
 * `search_use` — `pdp`/`category`/`suggestion` are wired from `predictive-search.js`'s existing
 * `#handleModalClick` (via `data-resource-type` on `.resource-card`, or `.predictive-search-
 * results__pill` for suggested queries); `search` fires from a generic `submit` listener matching
 * any form posting to `Theme.routes.search_url` (covers both the header search modal and the
 * search-results page's own re-search box, which are two unrelated components). Pages/articles
 * predictive results have no `search_action` value in the doc, so clicking those is untracked.
 * `no_search_results` reads `searchNoResults`/`searchTerms` seeded onto `GTMConfig` by
 * `gtm-valantic.liquid` (`search.performed and search.results_count == 0`) — needed because
 * `sections/search-results.liquid` silently falls back to a merchant-configured collection on zero
 * results instead of rendering any visible "no results" state, so there's nothing in the DOM to
 * detect this from client-side.
 * `resolveMenuListContext`/breadcrumb-driven category overrides read `[data-header-menu-hierarchy]`,
 * rendered by `gtm-valantic.liquid` itself (not `sections/breadcrumbs.liquid`, whose own copy of
 * this JSON is gated behind that section's visual enabled state) — see the comment there.
 *
 * @module gtm
 */

import { ThemeEvents, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents } from '@shopify/events';

const cfg = /** @type {any} */ (window.GTMConfig);
if (!cfg) throw new Error('[GTM] window.GTMConfig not found');

/**
 * Version of this tracking script, bumped on breaking changes to the pushed event/field shapes
 * (doc: `application_version` — "so we can track breaking changes"). Not the theme version.
 * @constant {string}
 */
const GTM_SCRIPT_VERSION = '1.0.0';

const GTM_AFFILIATION = cfg.affiliation;
const GTM_BUSINESS_VERTICAL = cfg.businessVertical;

/** @constant {{ item_list_id: string, item_list_name: string }} */
const CART_LIST_CONTEXT = cfg.cartListContext;

/** Active shop currency (Shopify global overrides Liquid default). */
const CURRENCY = window?.Shopify?.currency?.active ?? cfg.currency;

/** Shopify `template.name` from Liquid (e.g. `product`, `cart`, `index`). */
const PAGE_TYPE = cfg.pageType;

/**
 * Reads logged-in customer id/email from `#gtm-customer-ids`, removes the node (not kept on
 * `window` — email is PII).
 * @returns {{ accountId: string, email: string }}
 */
function consumeCustomerIds() {
  const el = document.getElementById('gtm-customer-ids');
  if (!el) return { accountId: '', email: '' };

  let data;
  try {
    data = JSON.parse(el.textContent || '{}');
  } catch {
    console.warn('[GTM] Invalid customer ids JSON');
    el.remove();
    return { accountId: '', email: '' };
  }

  el.remove();
  return { accountId: String(data.accountId ?? ''), email: String(data.email ?? '') };
}

/** One-time customer ids for the initial pageview (not on `window.GTMConfig`). */
const customerIds = consumeCustomerIds();

/**
 * Maps Shopify template names to GA4 `page.type` values.
 * @type {Record<string, string>}
 */
const PAGE_TYPE_MAP = {
  404: '404',
  article: 'article',
  blog: 'blog',
  cart: 'cart',
  collection: 'categorypage',
  gift_card: 'gift_card',
  index: 'homepage',
  product: 'productpage',
  search: 'search',
  'customers/login': 'login',
  'customers/register': 'sign_up',
};

/**
 * Selectors used to find clickable controls inside popups for `popup_cta`. Includes `a.button`
 * because the theme renders several real CTAs as anchor tags styled as buttons (e.g. the empty
 * cart drawer's "continue shopping" link, `snippets/cart-products.liquid`) — a plain `a` would be
 * too broad (footer/nav links aren't CTAs), so this only matches button-styled ones.
 * @constant {string[]}
 */
const BUTTON_SELECTORS = ['button', '.text-button', '[role="button"]', 'input[type="button"]', 'input[type="submit"]', 'a.button'];

/** Product-list containers rendered by collection/search grids and reusable product-list sections. */
const PRODUCT_LIST_SELECTOR = '.product-grid, .resource-list';

/** Matches Shopify Cart API mutation endpoints intercepted by `patchFetch`. */
const cartRegex = /\/cart\/(add|change|update)(\.js|\.json)?(\?.*)?$/;

/**
 * Groups cart lines by variant id, preserving order — used as the fallback match for lines that
 * have no exact `key` counterpart (see `matchCartLines`).
 * @param {{ id?: number, key?: string }[]} items
 * @returns {Map<string, Record<string, unknown>[]>}
 */
function groupByVariant(items) {
  const groups = new Map();
  for (const line of items || []) {
    const variantId = String(line?.id ?? line?.key ?? '');
    if (!groups.has(variantId)) groups.set(variantId, []);
    groups.get(variantId).push(line);
  }
  return groups;
}

/**
 * Matches old and new cart lines for diffing `add_to_cart`/`remove_from_cart`.
 *
 * Shopify's line `key` is `{variant_id}:{md5(line properties)}` and stays the same across a pure
 * quantity change, so an exact `key` match is the strongest available signal that two lines are
 * "the same" line — this preserves the correct removed/added identity (and the click-captured
 * metadata keyed on it, see `clickCapturedLineData`) even when duplicate-variant lines exist and
 * one of them is removed. Only lines with no exact `key` match on either side (e.g. the properties
 * hash itself changed) fall back to variant id + occurrence order, matching what's left pairwise.
 *
 * @param {{ id?: number, key?: string }[]} oldItemsList
 * @param {{ id?: number, key?: string }[]} newItemsList
 * @returns {[Record<string, unknown> | null, Record<string, unknown> | null][]}
 */
function matchCartLines(oldItemsList, newItemsList) {
  const oldRemaining = [...(oldItemsList || [])];
  const newRemaining = [...(newItemsList || [])];
  const pairs = [];

  for (let i = newRemaining.length - 1; i >= 0; i--) {
    const newLine = newRemaining[i];
    if (!newLine?.key) continue;
    const oldIndex = oldRemaining.findIndex((oldLine) => oldLine?.key === newLine.key);
    if (oldIndex === -1) continue;
    pairs.push([oldRemaining[oldIndex], newLine]);
    oldRemaining.splice(oldIndex, 1);
    newRemaining.splice(i, 1);
  }

  const oldGroups = groupByVariant(oldRemaining);
  const newGroups = groupByVariant(newRemaining);
  const variantIds = new Set([...oldGroups.keys(), ...newGroups.keys()]);
  variantIds.forEach((variantId) => {
    const oldLines = oldGroups.get(variantId) || [];
    const newLines = newGroups.get(variantId) || [];
    const max = Math.max(oldLines.length, newLines.length);
    for (let i = 0; i < max; i++) {
      pairs.push([oldLines[i] ?? null, newLines[i] ?? null]);
    }
  });

  return pairs;
}

/** `sessionStorage` key holding the pre-mutation cart snapshot. */
const PENDING_CART_EVENT = 'gtm_pending_cart_event';

/** Slim cart snapshot seeded from Liquid on first paint. */
let shopifyCart = slimCartSnapshot(cfg.shopifyCart);

/** Debounce timer for coalescing rapid `/cart/*` responses. */
let cartUpdateTimeout = null;

/**
 * Cart-page-only: `#gtm-cart-line-*` JSON captured at remove-button click time, keyed by
 * cart line `key`. Entries are read once by `normalizeCartLine` and deleted immediately after
 * — this is not a standing cache, just a same-tick handoff from click to the eventual diff,
 * needed because the theme's own section morph removes that DOM node as soon as the click's
 * own cart mutation resolves (before our debounced `/cart.js` diff gets a chance to read it).
 * Entries carry a capture timestamp: if the mutation never resolves (e.g. a network error), the
 * entry would otherwise never be read/deleted and leak for the rest of the session —
 * `pruneStaleClickCaptures` clears anything left over past `CLICK_CAPTURE_TTL_MS`.
 * @type {Map<string, { data: Record<string, unknown>, capturedAt: number }>}
 */
const clickCapturedLineData = new Map();

/** Longest a click-captured line waits for its mutation to resolve before being abandoned (ms). */
const CLICK_CAPTURE_TTL_MS = 10000;

/** Drops click-captured entries whose mutation never resolved within `CLICK_CAPTURE_TTL_MS`. */
function pruneStaleClickCaptures() {
  const cutoff = Date.now() - CLICK_CAPTURE_TTL_MS;
  for (const [key, entry] of clickCapturedLineData) {
    if (entry.capturedAt < cutoff) clickCapturedLineData.delete(key);
  }
}

/**
 * Resolves a Shopify template name to the GA4 page type string.
 * @param {string} value - `template.name` from Liquid
 * @returns {string}
 */
function getPageType(value) {
  return PAGE_TYPE_MAP[value] || 'other';
}

/**
 * Breakpoint label for `site.mediaquery` on pageview.
 * @returns {'small' | 'medium' | 'large'}
 */
function getCurrentBreakpoint() {
  const w = window.innerWidth;
  return w <= 600 ? 'small' : w <= 960 ? 'medium' : 'large';
}

/**
 * Reads cookie-consent state from Shopify's native Customer Privacy API, populated by the shop's
 * own (Shopify-hosted) cookie banner — no third-party CMP involved. `cookie_consent_essential` is
 * always `true`: strictly necessary cookies run regardless of consent, so there is nothing to ask
 * permission for. `functional`/`marketing` map to the API's `preferences`/`marketing` consent
 * categories. Falls back to `false` when `window.Shopify.customerPrivacy` isn't available yet (banner
 * script not injected/resolved) — the safe default is "not consented", never assumed consent.
 * @returns {{cookie_consent_essential: true, cookie_consent_functional: boolean, cookie_consent_marketing: boolean}}
 */
function getCookieConsent() {
  const privacy = window.Shopify?.customerPrivacy;
  return {
    cookie_consent_essential: true,
    cookie_consent_functional: Boolean(privacy?.preferencesProcessingAllowed?.()),
    cookie_consent_marketing: Boolean(privacy?.marketingAllowed?.()),
  };
}

/**
 * Shopify's cookie banner dispatches `visitorConsentCollected` on `document` both once the
 * visitor's stored/default consent is first resolved and again every time they change their
 * choice. Re-pushing the flat `cookie_consent_*` keys (no `event` name, so no trigger fires) lets
 * any GTM Data Layer Variable reading them pick up the fresh value immediately, without needing a
 * page reload — this is the only way these fields become accurate, since the initial `pageview`
 * push can happen before the banner script has resolved a stored decision.
 */
function cookieConsentUpdateListener() {
  document.addEventListener('visitorConsentCollected', () => {
    window.dataLayer.push(getCookieConsent());
  });
}

/**
 * `window.Shopify.customerPrivacy` is not injected by default on every plan/theme — it needs the
 * Consent Tracking API feature explicitly loaded first (confirmed on this store: `window.Shopify`
 * exists with `loadFeatures` but no `customerPrivacy` until this runs). Once loaded, re-pushes
 * `cookie_consent_*` so the initial `pageview`'s pre-load snapshot (always `false` for
 * functional/marketing before this resolves) gets corrected without waiting for a
 * `visitorConsentCollected` change event, which only fires on an actual consent decision.
 */
function loadCookieConsentApi() {
  if (!window.Shopify?.loadFeatures) return;
  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (error) return;
    window.dataLayer.push(getCookieConsent());
  });
}

/**
 * Converts Shopify integer cents to a major-unit float with two decimals.
 * @param {number} price - Price in cents
 * @returns {number}
 */
function formatPrice(price) {
  return +(price / 100).toFixed(2);
}

/**
 * Unique id per `dataLayer` push, for Server-Side Tagging event deduplication (per
 * the Measurement Plan doc's dictionary sheet). Not applied to the
 * `{ ecommerce: null }` reset push, which carries no `unique_event_id` in the doc's own schema.
 * @returns {string}
 */
function generateUniqueEventId() {
  return `${Date.now()}_${Math.floor(Math.random() * 1e9)}`;
}

/**
 * Canonical `items[]` key order, matching the Measurement Plan doc's `items` sheet order (plus
 * `quantity`, slotted next to `index`/`stock` where it reads naturally) — purely cosmetic, so
 * `dataLayer` entries are easy to scan in devtools while debugging. `item_category`/`item_category2..5`
 * are listed individually because `setItemCategories()` (used by several upstream item-building
 * functions) adds them via bracket-assignment after the rest of the object already exists, which
 * would otherwise always push them to the end of the key order.
 * @constant {string[]}
 */
const GTM_ITEM_FIELD_ORDER = [
  'item_name',
  'item_id',
  'affiliation',
  'coupon',
  'currency',
  'discount',
  'price',
  'item_variant',
  'item_brand',
  'item_category',
  'item_category2',
  'item_category3',
  'item_category4',
  'item_category5',
  'item_list_id',
  'item_list_name',
  'index',
  'quantity',
  'stock',
  'main_category',
  'subcategory',
  'sale',
];

/**
 * Rebuilds a GA4 item with its keys in `GTM_ITEM_FIELD_ORDER`, appending any (unexpected) leftover
 * keys at the end so nothing is ever silently dropped.
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
function orderGtmItemFields(item) {
  const ordered = {};
  for (const key of GTM_ITEM_FIELD_ORDER) {
    if (key in item) ordered[key] = item[key];
  }
  for (const key of Object.keys(item)) {
    if (!(key in ordered)) ordered[key] = item[key];
  }
  return ordered;
}

/**
 * Pushes a GA4/GTM event, stamping `unique_event_id` on every push. Reorders `ecommerce.items`
 * (see `orderGtmItemFields`) here, centrally, so every event call site — regardless of which
 * upstream function built its items — pushes them in the same, easy-to-debug key order.
 * @param {Record<string, unknown>} payload
 */
function pushEvent(payload) {
  const ecommerce = /** @type {any} */ (payload)?.ecommerce;
  if (ecommerce && Array.isArray(ecommerce.items)) {
    payload = { ...payload, ecommerce: { ...ecommerce, items: ecommerce.items.map(orderGtmItemFields) } };
  }
  window.dataLayer.push({ unique_event_id: generateUniqueEventId(), ...payload });
}

/**
 * Wraps an event-handler callback so a thrown exception is caught and logged instead of
 * propagating. The browser already isolates one throwing `addEventListener` callback from
 * others, so this isn't needed for page-crash safety — it's for the handlers with the most
 * chained work per call (`select_item`'s full price/category resolution chain, the cart-line
 * click capture feeding the add/remove diff, Swym's wishlist callback running outside normal DOM
 * event dispatch), where a failure would otherwise be entirely silent.
 * @param {string} label - Logged with the error, to identify which handler failed
 * @param {(...args: any[]) => void} fn
 * @returns {(...args: any[]) => void}
 */
function safeHandler(label, fn) {
  return (...args) => {
    try {
      fn(...args);
    } catch (e) {
      console.error(`[GTM] ${label} failed`, e);
    }
  };
}

/**
 * Human-readable variant label from cart line or static GTM JSON. Every Bolsius product is a
 * single-variant "Default Title" product — color/scent differences are separate products, not
 * Shopify variant options — so that placeholder is filtered out everywhere it could appear, and
 * `item_color` (the `channelengine.Kleur` metafield, resolved in the Liquid seed) is the fallback
 * that actually carries this product's "variant" per the Measurement Plan doc's intent.
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
function getItemVariantLabel(item) {
  const isRealValue = (v) => typeof v === 'string' && v && v !== 'Default Title';
  if (isRealValue(item?.variant_title)) return item.variant_title;
  const opts = item?.options_with_values;
  if (Array.isArray(opts) && opts.length) {
    const label = opts
      .map((o) => o.value)
      .filter(isRealValue)
      .join(' / ');
    if (label) return label;
  }
  if (Array.isArray(item?.variant_options) && item.variant_options.length) {
    const label = item.variant_options.filter(isRealValue).join(' / ');
    if (label) return label;
  }
  if (isRealValue(item?.item_variant)) return item.item_variant;
  return isRealValue(item?.item_color) ? item.item_color : '';
}

/**
 * Normalizes Shopify discount allocation objects (line-level vs cart-level).
 * @param {Record<string, unknown>} allocation
 * @returns {Record<string, unknown>}
 */
function resolveAllocation(allocation) {
  return allocation?.discount_application ?? allocation ?? {};
}

/**
 * Comma-separated discount codes applied to a cart line, or empty when none.
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
function getLineItemCoupon(item) {
  const codes = (item?.line_level_discount_allocations || [])
    .map(resolveAllocation)
    .filter((d) => d?.type === 'discount_code' && d?.title)
    .map((d) => d.title);
  return codes.length ? [...new Set(codes)].join(', ') : '';
}

/**
 * Discount amount (unit price, incl. VAT) for a cart line.
 * @param {Record<string, unknown>} item
 * @returns {number}
 */
function getLineDiscount(item) {
  const finalCents = item?.final_price ?? item?.discounted_price ?? item?.price ?? 0;
  const originalCents = Math.max(
    finalCents,
    item?.compare_at_price ?? 0,
    item?.original_unit_price ?? 0,
    item?.original_price ?? 0
  );
  return formatPrice(originalCents - finalCents);
}

/**
 * Cart or static product JSON → full GA4 `items[]` object. Matches the Measurement Plan doc's
 * `items` sheet exactly, plus `quantity` (a standard GA4 item param needed for
 * `add_to_cart`/`remove_from_cart`/`purchase` value math, not itemized in the sheet the same way
 * `index` isn't either). `item_color` is read as an internal-only input for `item_variant`'s
 * fallback (see `getItemVariantLabel()`) — it is not itself an output field.
 * @param {Record<string, unknown>} item
 * @param {{ item_list_id?: string, item_list_name?: string }} [listContext]
 * @returns {Record<string, unknown>}
 */
function normalizeGtmItem(item, listContext = CART_LIST_CONTEXT) {
  if (!item) return {};
  const discount = getLineDiscount(item);
  const categories = [...new Set(item.category_list || [])].slice(0, 5);
  const normalized = {
    item_name: item.item_name || item.product_title || item.title,
    item_id: item.item_id || String(item.product_id || ''),
    affiliation: GTM_AFFILIATION,
    coupon: item.coupon || getLineItemCoupon(item),
    currency: CURRENCY,
    discount,
    price: formatPrice(item.final_price ?? item.price),
    item_variant: getItemVariantLabel(item),
    item_brand: item.item_brand || item.vendor || '',
    main_category: resolveMainCategory(item.list_candidates) || item.main_category || '',
    subcategory: resolveSubcategory(item.list_candidates) || item.subcategory || '',
    item_list_id: item.item_list_id || listContext.item_list_id || CART_LIST_CONTEXT.item_list_id,
    item_list_name: item.item_list_name || listContext.item_list_name || CART_LIST_CONTEXT.item_list_name,
    quantity: item.quantity ?? 1,
    stock: Boolean(item.stock ?? item.variant_inventory_quantity ?? false),
    sale: discount > 0,
  };

  setItemCategories(normalized, categories);
  return normalized;
}

/**
 * Total cart value in major units, for `ecommerce.value` on `view_cart`.
 * @param {Record<string, unknown>} cart
 * @returns {number}
 */
function getCartValue(cart) {
  return formatPrice(cart?.total_price ?? cart?.value ?? 0);
}

/**
 * Minimal cart state kept in memory and `sessionStorage` for diffing.
 * @param {Record<string, unknown> | null | undefined} cart
 * @returns {{ currency?: string, total_price?: number, items_subtotal_price?: number, item_count?: number, items: unknown[] }}
 */
function slimCartSnapshot(cart) {
  if (!cart) return { items: [] };
  return {
    currency: cart.currency,
    total_price: cart.total_price,
    items_subtotal_price: cart.items_subtotal_price,
    item_count: cart.item_count,
    items: (cart.items || []).map((line) => ({
      key: line.key,
      id: line.id,
      product_id: line.product_id,
      sku: line.sku || '',
      quantity: line.quantity,
      final_price: line.final_price ?? line.price,
      product_title: line.product_title || '',
      variant_title: line.variant_title || '',
      vendor: line.vendor || '',
    })),
  };
}

/**
 * Parses pre-rendered GTM JSON from a DOM node (typically `<script type="application/json">`).
 * @param {Element | null} el
 * @param {string} [warnLabel] - Context for console.warn on parse failure
 * @returns {Record<string, unknown> | null}
 */
function parseGtmJsonElement(el, warnLabel = 'GTM JSON') {
  if (!(el instanceof Element)) return null;
  try {
    const raw = (el.textContent || '').trim();
    if (!raw || /Liquid error/i.test(raw)) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.warn(`[GTM] Invalid JSON in ${warnLabel}`, e);
    return null;
  }
}

/**
 * Parses the header menu hierarchy JSON — rendered by `sections/breadcrumbs.liquid` on any
 * template that section is placed on, when that markup has been added there. Null if
 * absent/invalid.
 *
 * Cached after the first call: the menu is static SSR content for the lifetime of the page, and
 * this is now called once per item (`resolveSubcategory`/`applyMenuListContext`) — on a PLP with
 * 20-40 product cards, re-querying and re-parsing the same DOM node that many times would be
 * pure waste.
 * @returns {{ items?: Array<{ title?: string, url?: string, items?: unknown[] }> } | null}
 */
let headerMenuHierarchyCached = false;
let cachedHeaderMenuHierarchy = null;
function getHeaderMenuHierarchy() {
  if (!headerMenuHierarchyCached) {
    cachedHeaderMenuHierarchy = parseGtmJsonElement(
      document.querySelector('script[data-header-menu-hierarchy]'),
      'header-menu-hierarchy'
    );
    headerMenuHierarchyCached = true;
  }
  return cachedHeaderMenuHierarchy;
}

/**
 * Collection handle from a `/collections/...` URL, e.g. `/nl/collections/dames` -> `dames`.
 * @param {string} url
 * @returns {string}
 */
function collectionHandleFromUrl(url) {
  return (String(url || '').match(/\/collections\/([^/?#]+)/i) || [])[1] || '';
}

/**
 * Titles from the menu root down to the first node (any depth) whose collection handle matches,
 * or `[]`.
 * @param {Array<{ title?: string, url?: string, items?: unknown[] }>} items
 * @param {string} targetHandle
 * @param {string[]} [trail]
 * @returns {string[]}
 */
function findMenuPathByHandle(items, targetHandle, trail = []) {
  for (const item of items || []) {
    if (!item || typeof item !== 'object') continue;
    const title = typeof item.title === 'string' ? item.title : '';
    const nextTrail = title ? [...trail, title] : trail;
    if (collectionHandleFromUrl(item.url) === targetHandle) return nextTrail;
    const found = findMenuPathByHandle(item.items, targetHandle, nextTrail);
    if (found.length) return found;
  }
  return [];
}

/**
 * `collection handle -> menu path` for every collection link in the menu, built by walking the
 * tree once and cached for the page's lifetime. `resolveSubcategory` looks up one product's
 * several candidate handles per card — on a PLP with 20-40 cards, re-walking the whole menu tree
 * per (card, candidate handle) pair (`findMenuPathByHandle`) redoes the same traversal repeatedly
 * for data that never changes within the page. First occurrence wins per handle, matching
 * `findMenuPathByHandle`'s own first-match behavior.
 * @returns {Map<string, string[]>}
 */
let menuHandlePathsCached = false;
const menuHandlePaths = new Map();
function indexMenuHandlePaths(items, trail = []) {
  for (const item of items || []) {
    if (!item || typeof item !== 'object') continue;
    const title = typeof item.title === 'string' ? item.title : '';
    const nextTrail = title ? [...trail, title] : trail;
    const handle = collectionHandleFromUrl(item.url);
    if (handle && !menuHandlePaths.has(handle)) menuHandlePaths.set(handle, nextTrail);
    indexMenuHandlePaths(item.items, nextTrail);
  }
}
function getMenuHandlePaths() {
  if (!menuHandlePathsCached) {
    const menu = getHeaderMenuHierarchy();
    if (menu?.items) indexMenuHandlePaths(menu.items);
    menuHandlePathsCached = true;
  }
  return menuHandlePaths;
}

/**
 * Sets `item_category`/`item_category2..` from `categories`, replacing any existing ones.
 * @param {Record<string, unknown>} item
 * @param {string[]} categories
 */
function setItemCategories(item, categories) {
  for (const key of Object.keys(item)) {
    if (/^item_category\d*$/.test(key)) delete item[key];
  }
  categories.forEach((title, i) => {
    item[i === 0 ? 'item_category' : `item_category${i + 1}`] = title;
  });
}

/**
 * Resolves `item_list_id`/`item_list_name`/`item_category*` for a product from its own collection
 * handles (`candidateHandles`), preferring the first one actually linked in the site menu — mirrors
 * the same preference used for PDP breadcrumbs. The display name and category path come from the
 * matched menu entries' own titles, not the collections' titles.
 * Null when the menu JSON is unavailable or no candidate matches — caller keeps its static default.
 * @param {string[]} candidateHandles
 * @returns {{ item_list_id: string, item_list_name: string, categories: string[] } | null}
 */
function resolveMenuListContext(candidateHandles) {
  if (!Array.isArray(candidateHandles) || !candidateHandles.length) return null;
  const menu = getHeaderMenuHierarchy();
  if (!menu?.items) return null;

  for (const handle of candidateHandles) {
    if (!handle) continue;
    const path = findMenuPathByHandle(menu.items, handle);
    if (path.length) return { item_list_id: handle, item_list_name: path[path.length - 1], categories: path };
  }
  return null;
}

/**
 * Finds the single deepest menu path across all candidate collection handles — shared lookup
 * behind `resolveSubcategory`/`resolveMainCategory`, since both read the same "most specific menu
 * placement" for a product, just at different depths of the same path.
 *
 * Deliberately does NOT reuse `resolveMenuListContext` itself: that function returns on the
 * FIRST candidate handle that matches anywhere in the menu, in the product's own collection
 * order — which can pick a shallower, less relevant match over a deeper, more specific one
 * further down the candidate list. This instead checks every candidate (via the cached
 * `getMenuHandlePaths` index — O(1) per candidate, not a fresh tree walk) and keeps the single
 * deepest path found.
 * @param {string[]} candidateHandles
 * @returns {string[]} Menu path titles, root first; empty when nothing matches.
 */
function findDeepestMenuPath(candidateHandles) {
  if (!Array.isArray(candidateHandles) || !candidateHandles.length) return [];
  const paths = getMenuHandlePaths();
  if (!paths.size) return [];

  let deepestPath = [];
  for (const handle of candidateHandles) {
    if (!handle) continue;
    const path = paths.get(handle);
    if (path && path.length > deepestPath.length) deepestPath = path;
  }
  return deepestPath;
}

/**
 * `subcategory` GA4 field: the deepest menu-nested collection title for a product (e.g. "The
 * perfect white shirt" nested under "Clothing" in the main menu) — there's no breadcrumb UI on
 * this theme to source this from, so it's resolved by matching `candidateHandles` (a product's
 * own collection handles) against the site's menu hierarchy, same input as
 * `resolveMenuListContext`. Purely additive: never touches `item_category*`/`item_list_id`/
 * `item_list_name`. Empty string when the deepest match is only one level deep (nothing more
 * specific than the top-level category) or nothing matches.
 * @param {string[]} candidateHandles
 * @returns {string}
 */
function resolveSubcategory(candidateHandles) {
  const path = findDeepestMenuPath(candidateHandles);
  return path.length > 1 ? path[path.length - 1] : '';
}

/**
 * `main_category` GA4 field: one menu level above `subcategory` in the same deepest matched path
 * (doc example: `main_category` "Sweaters" -> `subcategory` "Crewnecks"). Empty under the same
 * condition as `resolveSubcategory` — there's nothing above the top-level category, or no
 * candidate handle matched the menu at all.
 * @param {string[]} candidateHandles
 * @returns {string}
 */
function resolveMainCategory(candidateHandles) {
  const path = findDeepestMenuPath(candidateHandles);
  return path.length > 1 ? path[path.length - 2] : '';
}

/**
 * Overrides `item_list_id`/`item_list_name` and `item_category`/`item_category2..` on a GA4 item
 * using `resolveMenuListContext` against its own `list_candidates` (added by
 * `snippets/gtm-cart-line.liquid`), then strips that field — it's a resolution input, not a GA4
 * property. No-op (beyond the strip) when nothing matches, leaving the static defaults in place.
 *
 * Mutates `item` in place (same object identity) and also returns it — callers chain these
 * directly and rely on getting the same reference back, not a copy.
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>} The same `item` reference, mutated.
 */
function applyMenuListContext(item) {
  const resolved = resolveMenuListContext(item.list_candidates);
  if (resolved) {
    item.item_list_id = resolved.item_list_id;
    item.item_list_name = resolved.item_list_name;
    setItemCategories(item, resolved.categories);
  }
  delete item.list_candidates;
  delete item.category_list;
  return item;
}

/**
 * Reads pre-rendered GTM JSON from a hidden `.gtm-card-product` node or `#gtm-product-{id}`.
 *
 * @param {Node | number | string} product - DOM node, or product id for lookup
 * @returns {Record<string, unknown> | null}
 */
function getGtmData(product) {
  let node = null;
  if (product instanceof Node) {
    node = product.querySelector('.gtm-card-product');
  } else if (typeof product === 'number' || typeof product === 'string') {
    node = document.querySelector(`#gtm-product-${product}`);
  }
  if (!node) return null;
  return parseGtmJsonElement(node, '.gtm-card-product');
}

/**
 * Reads pre-rendered GTM JSON for a cart line from `#gtm-cart-line-{productId}-{variantId}-*`
 * (rendered on the cart page/drawer). Fallback for add/remove events triggered from the cart
 * page, where no `#gtm-product-{id}` card exists to enrich category/stock data from.
 *
 * @param {number | string} productId
 * @param {number | string} variantId
 * @returns {Record<string, unknown> | null}
 */
function getGtmCartLineDataByVariant(productId, variantId) {
  const node = document.querySelector(`[id^="gtm-cart-line-${productId}-${variantId}-"]`);
  return parseGtmJsonElement(node, `gtm-cart-line-${productId}-${variantId}`);
}

/**
 * Single cart mutation line for `ecommerce.items` on add/remove events.
 *
 * @param {Record<string, unknown>} line
 * @param {number} [quantity] - Mutation quantity (may differ from line.quantity)
 * @returns {Record<string, unknown>}
 */
function normalizeCartLine(line, quantity) {
  const capturedEntry = line.key ? clickCapturedLineData.get(line.key) : undefined;
  if (capturedEntry) clickCapturedLineData.delete(line.key);
  const staticGtmData =
    capturedEntry?.data ||
    getGtmData(line.product_id) ||
    getGtmData(line.id) ||
    getGtmCartLineDataByVariant(line.product_id, line.id) ||
    {};
  const listContext = {
    item_list_id: staticGtmData.item_list_id || CART_LIST_CONTEXT.item_list_id,
    item_list_name: staticGtmData.item_list_name || CART_LIST_CONTEXT.item_list_name,
  };
  const compareAtPriceCents = line.compare_at_price ?? staticGtmData.original_price ?? undefined;
  const base = { ...staticGtmData, ...line, compare_at_price: compareAtPriceCents };
  const payload = quantity != null ? { ...base, quantity } : base;
  // Cart mutations use the categories and list context rendered by the product/cart-line
  // Liquid seed. Do not override them from the navigation menu: a collection can be linked
  // under an unrelated or incorrectly configured menu label, which would make the same product
  // change category between PDP/wishlist and cart events.
  const normalized = normalizeGtmItem(payload, listContext);

  // Mutating this same line's product on its own PDP: reuse breadcrumb categories, but retain
  // the seed's product-list type (`product_page`) as required by the Measurement Plan doc.
  const isCurrentPdpProduct = PAGE_TYPE === 'product' && String(line.product_id) === String(cfg.productId);
  return isCurrentPdpProduct ? applyBreadcrumbCategories(normalized) : normalized;
}

/** Clears the previous `ecommerce` object before pushing a new GA4 ecommerce event. */
function resetEcommerce() {
  window.dataLayer.push({ ecommerce: null });
}

/**
 * Pushes `text_message` when theme surfaces a user-visible message.
 * @param {unknown} message
 */
function pushTextMessage(message) {
  const text = typeof message === 'string' ? message.trim() : '';
  if (!text) return;
  pushEvent({ event: 'text_message', message_text: text });
}

/**
 * `newsletter_subscription` from the `theme:newsletter_subscription` custom event.
 *
 * @param {{ form_id?: string, email?: string }} [detail]
 */
function pushNewsletterEvent(detail) {
  const { form_id: formId, email } = detail || {};
  if (!formId || !email) return;
  pushEvent({ event: 'newsletter_subscription', form_id: formId, email });
}

/**
 * Wraps `window.fetch` to detect cart mutations and schedule a `/cart.js` refresh.
 */
function patchFetch() {
  const previousFetch = window.fetch;
  window.fetch = async function (...args) {
    const [resource] = args;
    if (typeof resource !== 'string' || !cartRegex.test(resource)) {
      return previousFetch.apply(this, args);
    }

    // The real mutation runs once, outside any try/catch of ours — a tracking-side exception
    // must never cause us to re-issue it (that would add the item to the cart twice).
    const response = await previousFetch.apply(this, args);

    try {
      if (response.ok) {
        sessionStorage.setItem(PENDING_CART_EVENT, JSON.stringify(slimCartSnapshot(shopifyCart)));
        monitorCartUpdate();
      } else {
        console.warn(`[GTM] Fetch ${resource} failed: ${response.status}`);
      }
    } catch (error) {
      console.error('[GTM] Cart tracking error:', error);
    }

    return response;
  };
}

/** Debounced cart refresh after a mutation response. */
function monitorCartUpdate() {
  if (cartUpdateTimeout) clearTimeout(cartUpdateTimeout);
  cartUpdateTimeout = setTimeout(fetchCartData, 200);
}

/** Fetches full cart JSON and diffs against the pending snapshot. */
async function fetchCartData() {
  try {
    const response = await fetch('/cart.js?adw=1');
    if (response.ok) {
      compareCartData(await response.json());
    } else {
      console.error('[GTM] Failed to fetch /cart.js:', response.status);
    }
  } catch (error) {
    console.error('[GTM] Error fetching /cart.js:', error);
  } finally {
    pruneStaleClickCaptures();
  }
}

/**
 * Compares old and new cart lines and fires `add_to_cart` / `remove_from_cart`.
 *
 * @param {Record<string, unknown>} newCartData
 */
function compareCartData(newCartData) {
  const pendingSnapshot = sessionStorage.getItem(PENDING_CART_EVENT);
  let oldCartData = { items: [] };
  if (pendingSnapshot) {
    try {
      oldCartData = JSON.parse(pendingSnapshot);
    } catch (e) {
      console.warn('[GTM] Invalid pending cart snapshot JSON', e);
    }
  }
  newCartData = newCartData || { items: [] };

  matchCartLines(oldCartData.items, newCartData.items).forEach(([oldItem, newItem]) => {
    if (newItem && !oldItem) {
      pushCartMutationEvent('add_to_cart', newItem.quantity, newItem, newCartData);
    } else if (newItem && oldItem && newItem.quantity > oldItem.quantity) {
      pushCartMutationEvent('add_to_cart', newItem.quantity - oldItem.quantity, newItem, newCartData);
    } else if (oldItem && !newItem) {
      pushCartMutationEvent('remove_from_cart', oldItem.quantity, oldItem, newCartData);
    } else if (oldItem && newItem && oldItem.quantity > newItem.quantity) {
      pushCartMutationEvent('remove_from_cart', oldItem.quantity - newItem.quantity, newItem, newCartData);
    }
  });

  sessionStorage.removeItem(PENDING_CART_EVENT);
  shopifyCart = slimCartSnapshot(newCartData);
}

/**
 * Pushes add/remove with one GA4 item and full CDP cart snapshot.
 *
 * @param {'add_to_cart' | 'remove_from_cart'} eventName
 * @param {number} quantity
 * @param {Record<string, unknown>} item
 * @param {Record<string, unknown>} cartState
 */
function pushCartMutationEvent(eventName, quantity, item, cartState) {
  const mutationItem = normalizeCartLine(item, quantity);
  const unitPrice = mutationItem.price ?? formatPrice(item.final_price ?? item.price);
  resetEcommerce();
  pushEvent({
    event: eventName,
    ecommerce: {
      currency: cartState?.currency || CURRENCY,
      value: +(unitPrice * quantity).toFixed(2),
      items: [mutationItem],
    },
  });
}

/**
 * Collapses whitespace in element text for navigation labels.
 * @param {Element | null | undefined} el
 * @returns {string}
 */
function trimText(el) {
  return (el?.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Best-effort label for buttons and footer links (`navigation_text`, popups).
 * @param {Element} el
 * @returns {string}
 */
function getElText(el) {
  return (
    trimText(el) ||
    el.dataset.navText ||
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    el.querySelector('[data-content]')?.dataset.content ||
    el.getAttribute('class') ||
    trimText(el.parentNode)
  );
}

/**
 * Stable popup identifier for dialog and overlay events.
 * @param {Element} dialog
 * @returns {string}
 */
function getDialogId(dialog) {
  return dialog.getAttribute('aria-labelledby') || dialog.getAttribute('class');
}

/**
 * `dialog:open`/`dialog:close` fire on the `dialog-component` wrapper (e.g.
 * `<cart-drawer-component>`), not on its native `<dialog>` child — unwraps to that `<dialog>`
 * (exposed as the wrapper's `refs.dialog`, a public property on the theme's `Component` base
 * class), since that's the element carrying the meaningful id/class.
 * @param {EventTarget | null} target
 * @returns {Element | null}
 */
function resolveDialogElement(target) {
  if (!(target instanceof Element)) return null;
  const dialog = /** @type {any} */ (target).refs?.dialog;
  if (dialog instanceof Element) return dialog;
  return target.querySelector('dialog') || target;
}

/**
 * @param {'popup_visible' | 'popup_close' | 'popup_cta'} eventType
 * @param {string} id
 */
function setDataLayerDialog(eventType, id) {
  pushEvent({ event: eventType, popup_id: id });
}

/**
 * @param {'popup_visible'} eventType
 * @param {string} id
 */
function setDataLayerPopUp(eventType, id) {
  if (id) pushEvent({ event: eventType, popup_id: id });
}

/**
 * @param {Element | null} popUp
 * @returns {boolean}
 */
function isVisualPopUp(popUp) {
  if (!popUp) return false;
  const cs = window.getComputedStyle(popUp);
  return cs.display !== 'none' && cs.visibility !== 'hidden';
}

/**
 * Resolves an id for cookie banner or theme dialogs.
 * @param {Element} popUp
 * @returns {string}
 */
function getIdPopUp(popUp) {
  return (
    popUp.id ||
    popUp.closest('[id]')?.id ||
    popUp.querySelector('form[data-id]')?.dataset.id ||
    popUp.querySelector('form[data-testid]')?.dataset.testid ||
    popUp.getAttribute('class') ||
    'unknown_popup'
  );
}

/**
 * Tags buttons inside a dialog so the global click handler can emit `popup_cta`. Excludes
 * `.close-button` (the theme's consistent class for every dialog's own close control, e.g.
 * `snippets/header-actions.liquid`, `blocks/popup-link.liquid`) — closing a popup is not "using"
 * its CTA, and without this exclusion every `popup_close` would also fire a spurious `popup_cta`.
 */
function markButtons(popUp) {
  popUp.querySelectorAll(BUTTON_SELECTORS).forEach((el) => {
    if (el.closest('.close-button')) return;
    el.dataset.layerPopupCta = getDialogId(popUp);
  });
}

/**
 * Waits for an element via `MutationObserver`. Optional `onChange` runs once on attribute change, then disconnects.
 * Resolves `null` (and disconnects the observer) after `timeoutMs` if the element never appears —
 * without this, an element that never shows up (e.g. a consent banner blocked/never configured)
 * would leave the observer watching the whole `root` subtree for the rest of the page's life.
 *
 * @param {string | Node} selector
 * @param {{ root?: Element, onChange?: (el: Element) => void, timeoutMs?: number }} [options]
 * @returns {Promise<Element | null>}
 */
function observerHandler(selector, { root = document, onChange, timeoutMs = 10000 } = {}) {
  let visitedEl = false;
  return new Promise((resolve) => {
    let resolved = false;
    let timeoutId;
    const predicate = selector instanceof Node ? () => selector : () => root.querySelector(selector);

    const finish = (el) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeoutId);
      try {
        domObserver.disconnect();
      } catch {
        /* observer may already be disconnected */
      }
      if (el && typeof onChange === 'function') {
        const attrObserver = new MutationObserver(() => {
          if (visitedEl) return;
          visitedEl = true;
          onChange(el);
          attrObserver.disconnect();
        });
        attrObserver.observe(el, {
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
        });
      }
      resolve(el);
    };

    const immediate = predicate();
    if (immediate) return finish(immediate);

    const domObserver = new MutationObserver(() => {
      const el = predicate();
      if (el) finish(el);
    });
    domObserver.observe(root, { childList: true, subtree: true });
    timeoutId = setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Pushes initial `pageview`, matching the Measurement Plan doc's pageview
 * event schema exactly: `user` carries only `logged_in`/`account_id`/`type`, `site` only
 * `language`/`mediaquery`/`environment`, `page` only `type`/`load`. On the PDP, the doc's
 * `product` object is added with `stock` only — `review_quantity` is skipped, this store has no
 * reviews app. `application.version` is this tracking script's own version (`GTM_SCRIPT_VERSION`),
 * not the theme's — there is no theme version number to report. `cookie_consent_essential/
 * functional/marketing` (the doc's global variable table, not shown in its literal pageview
 * sample) are flat root-level keys sourced from `getCookieConsent()` — see
 * `cookieConsentUpdateListener` for how they stay fresh after this initial snapshot.
 */
function pushDataLayerEvents() {
  const { customer, site } = cfg;

  const payload = {
    event: 'pageview',
    ...getCookieConsent(),
    user: {
      logged_in: customer.loggedIn,
      account_id: customer.loggedIn ? customerIds.accountId : '',
      type: customer.type,
    },
    application: {
      id: 'main',
      store: site.store,
      version: GTM_SCRIPT_VERSION,
    },
    site: {
      language: site.language,
      mediaquery: getCurrentBreakpoint(),
      environment: site.environment,
    },
    page: {
      type: getPageType(PAGE_TYPE),
      load: 'regular',
    },
  };

  if (PAGE_TYPE === 'product') {
    const gtmData = getGtmData(cfg.productId);
    if (gtmData) payload.product = { stock: Boolean(gtmData.stock) };
  }

  pushEvent(payload);
}

/**
 * localStorage key remembering the last `customer.id` we've already fired `login` for. Shopify's
 * hosted Customer Accounts (login.shopify.com) run no theme JS during the actual login, so there
 * is no discrete "login submitted" moment to hook into from the storefront — this infers a login
 * from the anonymous→logged-in transition across page loads instead.
 * @constant {string}
 */
const LOGIN_TRACKED_KEY = 'gtm_last_logged_in_customer_id';

/**
 * Pushes GA4 `login` exactly once per anonymous→logged-in transition (doc: "when the user
 * succesfully logs in"). Compares the current `customer.id` against the last one we tracked in
 * `localStorage`:
 *  - logged out now → clears the flag, so a future login (even by the same account) fires again.
 *  - logged in, same id as last tracked → already fired for this login, no-op.
 *  - logged in, different/no prior id → new login, push once and remember this id.
 */
function trackLoginIfNeeded() {
  if (!customerIds.accountId) {
    localStorage.removeItem(LOGIN_TRACKED_KEY);
    return;
  }

  if (localStorage.getItem(LOGIN_TRACKED_KEY) === customerIds.accountId) return;

  localStorage.setItem(LOGIN_TRACKED_KEY, customerIds.accountId);
  pushEvent({
    event: 'login',
    account_id: customerIds.accountId,
    organization_id: '',
    account_type: cfg.customer.type,
    email: customerIds.email,
  });
}

/** Cart errors, theme messages, validation, and newsletter custom events. */
function errorEventHandler() {
  document.addEventListener(StandardEvents.cartError, (e) => {
    pushTextMessage(/** @type {any} */ (e)?.error || 'Error occurred');
  });
  document.addEventListener(ThemeEvents.textMessage, (e) => {
    pushTextMessage(e?.detail?.message);
  });
  document.addEventListener(ThemeEvents.validationMessage, (e) => {
    const text = typeof e?.detail?.validation_text === 'string' ? e.detail.validation_text.trim() : '';
    if (!text) return;
    pushEvent({
      event: 'validation_message',
      validation_text: text,
      validation_field: e.detail.validation_field || '',
    });
  });
  document.addEventListener(ThemeEvents.newsletterSubscription, (e) => pushNewsletterEvent(e?.detail));
}

/** Global `popup_cta` from elements marked in open dialogs. */
function generalButtonsClick() {
  document.addEventListener('click', (e) => {
    const el = e.target.closest(BUTTON_SELECTORS.join(', '));
    if (!el) return;
    if (el.dataset?.layerPopupCta) setDataLayerDialog('popup_cta', el.dataset.layerPopupCta);
  });
}

/** `navigation_use` from header menu/drawer link clicks. */
function navigationListeners() {
  document.addEventListener(ThemeEvents.navigationClick, ({ detail }) => {
    const navigation_text = detail?.navigation_text;
    if (!navigation_text) return;
    pushEvent({ event: 'navigation_use', navigation_text, navigation_level: detail.navigation_level });
  });
}

/**
 * Reshapes Shopify's `sort_by` slug (`price-descending`) into the doc's `<key> <direction>`
 * example format (`price desc`) — splits the `-ascending`/`-descending` suffix into `asc`/`desc`.
 * Slugs with no direction (`manual`, `most-relevant`, `best-selling`) pass through unchanged.
 * @param {string} sortBy
 * @returns {string}
 */
function normalizeSortOption(sortBy) {
  if (sortBy.endsWith('-ascending')) return `${sortBy.slice(0, -'-ascending'.length)} asc`;
  if (sortBy.endsWith('-descending')) return `${sortBy.slice(0, -'-descending'.length)} desc`;
  return sortBy;
}

/** `sort_use` from the PLP sort dropdown. */
function sortUseListener() {
  document.addEventListener(ThemeEvents.sortUse, ({ detail }) => {
    const sort_option = detail?.sort_option;
    if (!sort_option) return;
    pushEvent({ event: 'sort_use', sort_option: normalizeSortOption(sort_option) });
  });
}

/**
 * Shopify's `availability` facet is always the boolean value `1`/`0`, regardless of storefront
 * language — this is Shopify's own filter convention, not merchant-entered content, so mapping it
 * to a fixed English label (doc: filter_option "always English") is safe here in a way it isn't
 * for merchant-entered facets like color/size (see module doc comment).
 * @param {string} filterGroup
 * @param {string} filterOption
 * @returns {string}
 */
function normalizeFilterOption(filterGroup, filterOption) {
  if (filterGroup !== 'availability') return filterOption;
  return filterOption === '1' ? 'in_stock' : filterOption === '0' ? 'out_of_stock' : filterOption;
}

/**
 * This store's PLP filters are metafields from a ChannelEngine product feed integration
 * (`filter.p.m.channelengine.*`), so `filter_group` is that metafield's own key — merchant/feed
 * data, not a fixed Shopify param. Most of those keys are already English (`Height_in_mm`,
 * `Width_in_mm`, `Burning_Time_in_Hours`); `Kleur`/`Geur` are the two known Dutch ones (doc
 * requires `filter_group` "always English"). Falls through unchanged for anything not in this
 * map, rather than guessing a translation for a key we haven't seen.
 * @type {Record<string, string>}
 */
const FILTER_GROUP_TRANSLATIONS = {
  Kleur: 'color',
  Geur: 'scent',
};

/**
 * @param {string} filterGroup
 * @returns {string}
 */
function normalizeFilterGroup(filterGroup) {
  return FILTER_GROUP_TRANSLATIONS[filterGroup] || filterGroup;
}

/**
 * `filter_use` from the PLP filter checkboxes. `filter_option` is NOT translated to English here
 * (beyond the fixed `availability` 1/0 case) — for `Kleur`/`Geur` it's merchant-entered product
 * data (e.g. "Antraciet"), and there's no English source for that data in the theme/feed today.
 */
function filterUseListener() {
  document.addEventListener(ThemeEvents.filterUse, ({ detail }) => {
    const filter_group = detail?.filter_group;
    const filter_option = detail?.filter_option;
    if (!filter_group || !filter_option) return;
    pushEvent({
      event: 'filter_use',
      filter_group: normalizeFilterGroup(filter_group),
      filter_option: normalizeFilterOption(filter_group, filter_option),
    });
  });
}

/**
 * `search_use` from predictive search result clicks (`theme:search_use`, `search_action` in
 * `pdp`/`category`/`suggestion`) and from any search form submission (`search_action: 'search'`)
 * — the latter is a plain `submit` listener rather than a dispatched event, since the header
 * search modal and the search-results-page's own search box are two unrelated components with
 * no shared owner to dispatch from; both are native GET forms posting to the same search route.
 */
function searchUseListener() {
  document.addEventListener(ThemeEvents.searchUse, ({ detail }) => {
    const search_action = detail?.search_action;
    if (!search_action) return;
    pushEvent({ event: 'search_use', search_action, search_query: detail.search_query || '' });
  });

  const searchPath = new URL(window.Theme?.routes?.search_url || '/search', location.origin).pathname;
  document.addEventListener('submit', (e) => {
    if (!(e.target instanceof HTMLFormElement)) return;
    if (new URL(e.target.action).pathname !== searchPath) return;
    const search_query = new FormData(e.target).get('q');
    if (typeof search_query !== 'string') return;
    pushEvent({ event: 'search_use', search_action: 'search', search_query });
  });
}

/** `no_search_results` when the search page found zero results for a submitted query. */
function pushNoSearchResultsEvent() {
  if (!cfg.searchNoResults) return;
  pushEvent({ event: 'no_search_results', search_query: cfg.searchTerms || '' });
}

/** `contact` from form submit or mailto/tel/contact-page links. */
function contactListeners() {
  document.addEventListener('click', (e) => {
    const submitBtn = e.target.closest(
      'contact-form-component button[type="submit"], contact-form-component input[type="submit"]'
    );
    if (submitBtn) {
      const form = submitBtn.closest('form');
      if (form?.checkValidity()) pushEvent({ event: 'contact', platform: 'form' });
      return;
    }
    const link = e.target.closest('a[href]');
    if (!link) return;
    const href = (link.getAttribute('href') || '').toLowerCase();
    const platform =
      (link.dataset.contactPlatform || '').trim().toLowerCase() ||
      (href.startsWith('mailto:') ? 'email' : '') ||
      (href.startsWith('tel:') ? 'phone' : '') ||
      (href.includes('/pages/contact') || /\/contact(\/|$|\?|#)/.test(href) ? 'contact_page' : '');
    if (platform) pushEvent({ event: 'contact', platform });
  });
}

/** `footer_click` for footer anchor clicks. */
function footerNavigationClickListener() {
  document.addEventListener('click', (e) => {
    const link = e.target.closest('footer a[href]');
    if (!link) return;
    const navigation_text = getElText(link);
    if (navigation_text) pushEvent({ event: 'footer_click', navigation_text });
  });
}

/**
 * Observes cookie banner, Usercentrics UI, and the geolocation redirect app for `popup_visible`.
 * Some of these render hidden in the initial HTML and only become visible later via a class/style
 * toggle (e.g. `#gls-popup`) — `observerHandler` resolves as soon as the element exists in the DOM,
 * even while still hidden, so `onChange` re-checks visibility once its class/style actually changes.
 */
function popUpListener() {
  const watchPopUp = (selector) => {
    observerHandler(selector, {
      onChange: (el) => {
        if (isVisualPopUp(el)) setDataLayerPopUp('popup_visible', getIdPopUp(el));
      },
    }).then((el) => {
      if (isVisualPopUp(el)) setDataLayerPopUp('popup_visible', getIdPopUp(el));
    });
  };

  watchPopUp('#shopify-pc__banner');
  watchPopUp('#usercentrics-cmp-ui');
  watchPopUp('#gls-popup');
}

/**
 * Static PDP/cart-line item JSON (rendered by `datalayer-product.liquid` / `gtm-cart-line.liquid`)
 * → GA4 `items[]` object, matching the field set in the Measurement Plan doc's `items` sheet
 * exactly. `main_category`/`subcategory` are computed here from `list_candidates`. Only
 * `category_list` (as a field)/`list_candidates`/`google_business_vertical`/
 * `discount_percentage`/`original_price`/`item_color` are inputs, not outputs — `item_color` is
 * only read for `item_variant`'s fallback, via `getItemVariantLabel()` further up the pipeline
 * (the cart-mutation path) or directly in the Liquid seed (this static path).
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
function formatItemPrices(item) {
  const price = formatPrice(item.price);
  const originalPrice = formatPrice(item.original_price);
  const discount = Math.max(0, +(originalPrice - price).toFixed(2));
  const subcategory = resolveSubcategory(item.list_candidates);
  const main_category = resolveMainCategory(item.list_candidates);
  const {
    category_list: _categoryList,
    list_candidates: _listCandidates,
    google_business_vertical: _businessVertical,
    discount_percentage: _discountPercentage,
    original_price: _originalPrice,
    item_color: _itemColor,
    index,
    ...rest
  } = item;
  return {
    ...rest,
    currency: CURRENCY,
    price,
    discount,
    sale: discount > 0,
    main_category,
    subcategory,
    index,
  };
}

/**
 * Collects and normalizes product cards, then pushes `view_item_list`.
 * Adapted from shopify-features/gtm/gtm-valantic.liquid.
 * @param {NodeListOf<Element> | Element[]} productLists
 */
function processProductElements(productLists) {
  const items = [];

  productLists.forEach((productList) => {
    productList.querySelectorAll('product-card').forEach((product, index) => {
      const item = getGtmData(product);
      if (!item) return;
      items.push(applyMenuListContext({ ...formatItemPrices(item), index }));
    });
  });

  if (!items.length) return;
  attachProductClickEvents();
  addDataLayerForItemList(items);
}

/**
 * `view_item_list` + `select_item` binding for a paginated list's newly appended page of cards
 * (`theme:results_appended`, dispatched by `paginated-list.js`'s `grid.append` in `#renderNextPage`
 * — "Load more" adds cards with no other notification, so without this they get neither
 * event). Indexes sequentially within this batch (0-based), matching how the initial page-load
 * batch is indexed within itself — `processProductElements` can't be reused directly here since
 * it resets the index per top-level container, and each appended card arrives as its own `<li>`
 * wrapper (`sections/main-collection.liquid`'s `ref="cards[]"`), not one shared container.
 * @param {Element[]} elements - The newly appended `<li ref="cards[]">` wrapper elements
 */
function processAppendedProductElements(elements) {
  const items = [];
  elements.forEach((el) => {
    el.querySelectorAll('product-card').forEach((product) => {
      const item = getGtmData(product);
      if (!item) return;
      items.push(applyMenuListContext({ ...formatItemPrices(item), index: items.length }));
    });
  });

  if (!items.length) return;
  attachProductClickEvents();
  addDataLayerForItemList(items);
}

/** Whether the single delegated `select_item` click listener has been installed. */
let productClickListenerBound = false;

/**
 * Binds `select_item` once, globally, in the capture phase — not per product-card element. A
 * per-element bubble-phase listener (the previous approach) can silently never fire if anything
 * between the click and `<product-card>` calls `stopPropagation()` (e.g. the slideshow arrows
 * inside the gallery's `<a class="contents">` overlay must do this themselves, otherwise clicking
 * "next image" would also navigate to the PDP) — capture on `document` sees every click before
 * any such bubble-phase propagation is stopped. Delegation also means product cards inserted later
 * (infinite scroll, re-rendered sections) are covered without re-attaching anything per element.
 *
 * That same "arrows sit inside the card's `<a>`" structure is also why arrow clicks must be
 * excluded here explicitly: this listener runs in the capture phase, before the arrows' own
 * bubble-phase `stopPropagation()` ever executes, so it would otherwise see every "next/previous
 * image" click as if the card link itself had been activated and fire a `select_item` for it.
 */
function attachProductClickEvents() {
  if (productClickListenerBound) return;
  productClickListenerBound = true;

  document.addEventListener(
    'click',
    safeHandler('select_item click handler', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('slideshow-arrows')) return;
      const link = event.target.closest('a[href]');
      if (!link) return;
      const product = link.closest('product-card');
      if (!product || !product.querySelector('.gtm-card-product')) return;

      const productList = product.closest(PRODUCT_LIST_SELECTOR);
      if (!productList) return;

      const index = Array.from(productList.querySelectorAll('product-card')).indexOf(product);
      const item = getGtmData(product);
      if (!item || index < 0) return;
      const normalizedItem = applyMenuListContext({ ...formatItemPrices(item), index });

      resetEcommerce();
      pushEvent({
        event: 'select_item',
        ecommerce: {
          currency: CURRENCY,
          value: normalizedItem.price,
          item_list_id: normalizedItem.item_list_id,
          item_list_name: normalizedItem.item_list_name,
          items: [normalizedItem],
        },
      });
    }),
    { capture: true }
  );
}

/** Pushes GA4 `view_item_list` for normalized products. */
function addDataLayerForItemList(items) {
  resetEcommerce();
  pushEvent({
    event: 'view_item_list',
    ecommerce: {
      item_list_id: items[0].item_list_id,
      item_list_name: items[0].item_list_name,
      items,
    },
  });
}

/** Pushes reusable product lists when they first enter the viewport. */
function observeDynamicProducts() {
  const observer = new IntersectionObserver(
    (entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observerHandler('product-card .gtm-card-product', { root: entry.target }).then((productData) => {
          if (productData) processProductElements([entry.target]);
        });
        currentObserver.unobserve(entry.target);
      });
    },
    { threshold: 0.1 }
  );

  document.querySelectorAll('.resource-list').forEach((element) => observer.observe(element));
}

/** Initializes collection/search grids and reusable product lists. */
function initProductListTracking() {
  if (PAGE_TYPE === 'collection' || PAGE_TYPE === 'search') {
    processProductElements(document.querySelectorAll('.product-grid'));
  }
  observeDynamicProducts();
  document.addEventListener(ThemeEvents.resultsAppended, (e) => processAppendedProductElements(e.detail?.elements || []));
}

/**
 * Dynamic parent breadcrumb items on the PDP, excluding "Home" (first) and the current product
 * (last), which `.breadcrumbs__list` always renders statically.
 * @returns {Element[]}
 */
function getBreadcrumbParentItems() {
  return Array.from(document.querySelectorAll('.breadcrumbs__list .breadcrumbs__item')).slice(1, -1);
}

/**
 * Visible breadcrumb trail on the PDP → GA4 item_category/item_category2..
 * @returns {string[]}
 */
function getBreadcrumbCategories() {
  return getBreadcrumbParentItems()
    .map((el) => el.querySelector('[itemprop="name"]')?.textContent.trim())
    .filter(Boolean);
}

/**
 * Overrides `item_category`/`item_category2..` on a GA4 item with the live breadcrumb trail —
 * unlike `category_list`'s raw (unordered) `product.collections` membership, this reflects the
 * actual navigable category path shown to the user. PDP `view_item` only.
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
function applyBreadcrumbCategories(item) {
  const categories = getBreadcrumbCategories();
  if (categories.length) setItemCategories(item, categories);
  return item;
}

/**
 * Overrides `item_list_id`/`item_list_name` on a GA4 item with the immediate parent category from
 * the live PDP breadcrumb trail — the list the product was actually found and clicked from.
 * Leaves the static value (`product.collections.first`, from Liquid) untouched when breadcrumbs
 * show no parent category, e.g. a direct/bookmarked visit. PDP `view_item` only.
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
function applyBreadcrumbListContext(item) {
  const lastParent = getBreadcrumbParentItems().pop();
  const href = lastParent?.querySelector('a.breadcrumbs__link')?.getAttribute('href') || '';
  const handle = collectionHandleFromUrl(href);
  const name = lastParent?.querySelector('[itemprop="name"]')?.textContent?.trim();
  if (!handle || !name) return item;

  item.item_list_id = handle;
  item.item_list_name = name;
  return item;
}

/**
 * `add_to_wishlist` for the given product, using the same static seed/formatting as `view_item`.
 * @param {number | string} productId
 */
function pushWishlistEvent(productId) {
  const gtmData = getGtmData(productId);
  if (!gtmData) return;
  const item = applyBreadcrumbListContext(applyBreadcrumbCategories(formatItemPrices(gtmData)));
  resetEcommerce();
  pushEvent({
    event: 'add_to_wishlist',
    ecommerce: { currency: CURRENCY, value: item.price, items: [item] },
  });
}

/**
 * `add_to_wishlist` from Swym's own `addedToWishlist` event (`window._swat.evtLayer`, confirmed
 * live), registered via `window.SwymCallbacks.push()` — Swym's documented pattern for code that
 * needs its SDK ready, since `_swat` doesn't exist until Swym's own script has loaded.
 * @param {number | string} productId
 */
function wishlistListener(productId) {
  const register = () => {
    const swat = window._swat;
    if (!swat?.evtLayer || !swat?.JSEvents?.addedToWishlist) return;
    swat.evtLayer.addEventListener(
      swat.JSEvents.addedToWishlist,
      safeHandler('add_to_wishlist (Swym callback)', () => pushWishlistEvent(productId))
    );
  };

  if (typeof window.SwymCallbacks?.push === 'function') {
    window.SwymCallbacks.push(register);
  }
}

/**
 * PDP-only: `view_item`, variant change, gallery, reviews.
 * Only registered when `GTMConfig.pageType === 'product'`.
 */
function initProductPageTracking() {
  const productId = cfg.productId;

  const gtmData = getGtmData(productId);
  if (gtmData) {
    const item = applyBreadcrumbCategories(formatItemPrices(gtmData));
    resetEcommerce();
    pushEvent({
      event: 'view_item',
      ecommerce: { currency: CURRENCY, value: item.price, items: [item] },
    });
  }

  wishlistListener(productId);

  let lastPhotoZoomAt = 0;
  let lastPhotoZoomIndex = -1;

  const setDataLayerMediaGallery = (eventType, index) => {
    pushEvent({ event: eventType, product: productId, index });
  };

  const pushPhotoZoom = (index) => {
    const now = Date.now();
    if (index === lastPhotoZoomIndex && now - lastPhotoZoomAt < 400) return;
    lastPhotoZoomIndex = index;
    lastPhotoZoomAt = now;
    setDataLayerMediaGallery('photo_zoom', index);
  };

  document.addEventListener(ThemeEvents.zoomMediaIndex, (e) => pushPhotoZoom(e.detail.index + 1));
  document.addEventListener(ThemeEvents.zoomMediaSelected, (e) => pushPhotoZoom(e.detail.index + 1));

  document.addEventListener(SlideshowSelectEvent.eventName, (e) => {
    const { slide, index, previousIndex, trigger, event } = e.detail;
    const i = index + 1;
    const gallery = slide?.closest('media-gallery');
    if (!gallery) return;
    // The zoom dialog nests its own `<slideshow-component>` inside the same `<media-gallery>` as
    // the main PDP gallery — scope to the slide's OWN slideshow, not `gallery`'s first match,
    // which could resolve to the wrong one of the two when both are present.
    const slideshow = slide?.closest('slideshow-component');
    const slideCount = slideshow?.slides?.length ?? 0;
    // `slideshow.js`'s own `onPointerUp` unconditionally dispatches `trigger: 'drag'` on every
    // pointerup on the gallery, even a plain click that never actually moved the slide (e.g.
    // clicking the current image to open the zoom dialog) — `previousIndex === index` in that
    // case, which is not a navigation and must not be reported as one.
    const isRealTransition = typeof previousIndex === 'number' && index !== previousIndex;
    const isLastSlide = isRealTransition && slideCount > 0 && index === slideCount - 1;

    if (event?.target.closest('slideshow-arrows')) {
      const action = event.target.closest('[ref]')?.getAttribute('ref');
      if (action === 'next' || action === 'previous') setDataLayerMediaGallery(`photo_${action}`, i);
    } else if (isRealTransition && (trigger === 'drag' || trigger === 'scroll')) {
      let action = index > previousIndex ? 'next' : 'previous';
      if (slideCount > 1) {
        if (previousIndex === slideCount - 1 && index === 0) action = 'next';
        else if (previousIndex === 0 && index === slideCount - 1) action = 'previous';
      }
      setDataLayerMediaGallery(`photo_${action}`, i);
    }
    if (isLastSlide) setDataLayerMediaGallery('photo_last', i);
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-bv-show="rating_summary"], [data-bv-show="reviews"]')) return;
    if (e.target.closest('[data-bv-show="inpage_submission"]')) return;
    pushEvent({ event: 'review_click' });
  });
}

/**
 * Cart template: `view_cart` with CDP snapshot + GA4 `items[]` (same shape as `view_item`).
 *
 * Looked up by line position (`data-line-index`), not by `product_id`/variant `id` — two cart
 * lines can share the same variant (e.g. personalized items with different line properties),
 * which would collide on a `product_id`-`id` DOM id.
 *
 * @param {number} index - 0-based position of this line in `cart.items`.
 */
function getGtmCartLineData(index) {
  const el = document.querySelector(`[data-line-index="${index}"]`);
  return parseGtmJsonElement(el, `gtm-cart-line[${index}]`);
}

/** GA4 `items[]` for cart page/drawer `view_cart` — reads `[data-line-index]`, formats like `view_item`. */
function buildViewCartItems(cart) {
  return (cart?.items || [])
    .map((line, index) => {
      const gtmData = getGtmCartLineData(index);
      if (!gtmData) return null;
      return { ...formatItemPrices(gtmData), quantity: line.quantity ?? 1 };
    })
    .filter(Boolean);
}

/**
 * Captures a cart line's `#gtm-cart-line-*` JSON at the moment its remove button is clicked
 * — before the theme's own click handler runs, so before its section morph can remove that
 * DOM node. Listens on `document` in the capture phase so it always runs first, regardless of
 * where the cart component attaches its own (bubbling-phase) listener.
 */
function captureCartLineOnRemoveClick() {
  document.addEventListener(
    'click',
    safeHandler('cart-line remove-click capture', (e) => {
      const button = e.target.closest('.cart-items__remove');
      if (!button) return;
      const key = button.closest('[data-key]')?.dataset.key;
      if (!key) return;
      const gtmData = parseGtmJsonElement(document.querySelector(`[data-line-key="${key}"]`), `gtm-cart-line[key=${key}]`);
      pruneStaleClickCaptures();
      if (gtmData) clickCapturedLineData.set(key, { data: gtmData, capturedAt: Date.now() });
    }),
    { capture: true }
  );
}

/**
 * `view_cart` from the current `shopifyCart` state — cart page load only. The Measurement Plan
 * explicitly scopes this to "a new page", not the cart drawer, so this must not be wired to the
 * drawer's `dialog:open` event.
 */
function pushViewCartEvent() {
  resetEcommerce();
  pushEvent({
    event: 'view_cart',
    ecommerce: {
      currency: shopifyCart?.currency || CURRENCY,
      value: getCartValue(shopifyCart),
      items: buildViewCartItems(shopifyCart),
    },
  });
}

function initCartPageTracking() {
  pushViewCartEvent();
}

/** Registers listeners shared on every template; branches for cart and PDP. */
function initGlobalTracking() {
  pushDataLayerEvents();
  trackLoginIfNeeded();
  errorEventHandler();
  generalButtonsClick();
  navigationListeners();
  sortUseListener();
  filterUseListener();
  searchUseListener();
  contactListeners();
  footerNavigationClickListener();
  popUpListener();
  cookieConsentUpdateListener();
  loadCookieConsentApi();
  captureCartLineOnRemoveClick();
  initProductListTracking();

  // `dialog:open`/`dialog:close` (`@theme/dialog`) are dispatched on the `dialog-component`
  // custom element (`this.dispatchEvent(...)` where `this` is the wrapper), not on the native
  // `<dialog>` inside it, and not bubbled to `document` — but the capture phase still reaches
  // `document` regardless of the event's `bubbles` flag, so listening here in capture mode
  // catches every dialog instance (cart drawer, search modal, quick-add) without attaching to
  // each one individually. `resolveDialogElement` unwraps to the actual `<dialog>` (`refs.dialog`
  // on the Component), which is what carries the meaningful id/class (e.g. `cart-drawer__dialog`).
  document.addEventListener(
    'dialog:open',
    (e) => {
      const dialog = resolveDialogElement(e.target);
      if (!dialog) return;
      markButtons(dialog);
      setDataLayerDialog('popup_visible', getDialogId(dialog));
    },
    { capture: true }
  );

  document.addEventListener(
    'dialog:close',
    (e) => {
      const dialog = resolveDialogElement(e.target);
      if (!dialog) return;
      setDataLayerDialog('popup_close', getDialogId(dialog));
    },
    { capture: true }
  );

  if (PAGE_TYPE === 'cart') initCartPageTracking();
  if (PAGE_TYPE === 'product') initProductPageTracking();
  if (PAGE_TYPE === 'search') pushNoSearchResultsEvent();
}

if (sessionStorage.getItem(PENDING_CART_EVENT)) {
  fetchCartData();
}

if (!window.__GTM_patchedFetch) {
  window.__GTM_patchedFetch = true;
  patchFetch();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGlobalTracking);
} else {
  initGlobalTracking();
}
